import rem from 'polished/lib/helpers/rem';

/**
 * Shared minimum column width system for the app's data tables.
 *
 * Every table column is sized from here instead of inventing a `spacing.base`
 * multiple at the call site, so a column holding the same kind of content is
 * the same width on the Service Events, Assets and Depots/Customers tables.
 *
 * A column is described by a `minWidth` (the floor it never shrinks below, so
 * the header label and its sort icon always fit on a single line) and an
 * optional `maxWidth` (the ceiling past which its content truncates and moves
 * into a tooltip). Values are plain pixel numbers; `columnWidth` converts them
 * to `rem` so they still scale with the root font size.
 */

/** Minimum-width steps, named by the kind of content the column holds. */
export const MIN_COLUMN_WIDTHS = {
  /** A single badge or tag with no label — e.g. the Depots "Info" column. */
  badge: 52,
  /** Four-character values — e.g. a model year. */
  tiny: 71,
  /** Short counts. */
  compact: 90,
  /** Identifiers and order numbers. */
  short: 120,
  /** Money, durations and other single-line values. */
  value: 130,
  /** Dates without a time. */
  date: 150,
  /** The default floor for a labelled text column. */
  default: 160,
  /** Names, statuses and other columns that read as a phrase. */
  wide: 180,
  /** Dates with a time, or a label plus a secondary line. */
  timestamp: 205,
  /** Addresses and account names. */
  wider: 220,
  /** Long free text. */
  widest: 300,
} as const;

/**
 * Class name the table container uses to clamp a cell's content wrapper to
 * `--cell-max-width`. See `components/NoWrapHeaderTable`.
 */
export const CLAMPED_CELL_CLASS = 'clamped-cell';

export type ColumnWidth = {
  minWidth: number;
  maxWidth?: number;
  /** Defaults to `top` so multi-line cells line up with their header. */
  verticalAlign?: 'top' | 'middle' | 'bottom';
};

type CellAttrs = {
  className?: string;
  // Not `CSSProperties`: the clamp is published as the `--cell-max-width`
  // custom property, which that type does not allow.
  style: Record<string, string | number | undefined>;
};

type ColumnWidthAttrs = {
  getCellAttrs: () => CellAttrs;
  getHeaderCellAttrs: () => CellAttrs;
};

/**
 * Builds the `getCellAttrs`/`getHeaderCellAttrs` pair a KDS `Table` column
 * needs to honour a min/max width.
 *
 * The floor goes on both the `th` and the `td` so the header stays aligned
 * with its data, and the ceiling is published as the `--cell-max-width` custom
 * property that `NoWrapHeaderTable` clamps the cell's content wrapper with.
 * The header is deliberately left unclamped — its label must never truncate.
 */
export function columnWidth({
  minWidth,
  maxWidth,
  verticalAlign = 'top',
}: ColumnWidth): ColumnWidthAttrs {
  const min = rem(minWidth);
  const max = maxWidth ? rem(maxWidth) : undefined;

  const clamp = max
    ? { maxWidth: max, '--cell-max-width': max }
    : /* istanbul ignore next -- @preserve */ {};

  return {
    getCellAttrs: () => ({
      ...(max ? { className: CLAMPED_CELL_CLASS } : {}),
      style: { verticalAlign, minWidth: min, ...clamp },
    }),
    getHeaderCellAttrs: () => ({ style: { minWidth: min } }),
  };
}

/**
 * Curries `columnWidth` over one of the per-page maps below, so a columns file
 * reads `widths('ALERTS')` instead of repeating the numbers.
 */
export const columnWidthsFor =
  <TKey extends string>(widths: Record<TKey, ColumnWidth>) =>
  (key: TKey): ColumnWidthAttrs =>
    columnWidth(widths[key]);

/**
 * A zero-content column that soaks up whatever horizontal space is left over.
 *
 * KDS renders `table { width: 100% }` with an automatic layout, so without a
 * filler the browser spreads leftover width across the real columns and pushes
 * them past their `maxWidth`. Append this last (before any sticky actions
 * column) and every other column settles between its min and max.
 */
export const spacerColumn = <TData extends { id?: string }>() => ({
  DataCell: /* istanbul ignore next -- @preserve */ () => null,
  name: 'spacer',
  title: '',
  getCellKey: ({ id }: TData) => `${id}-spacer`,
  getCellAttrs: () => ({ style: { width: '100%', minWidth: 0, padding: 0 } }),
  getHeaderCellAttrs: () => ({ style: { width: '100%', minWidth: 0 } }),
});

const {
  badge,
  tiny,
  compact,
  short,
  value,
  date,
  default: defaultWidth,
  wide,
  timestamp,
  wider,
  widest,
} = MIN_COLUMN_WIDTHS;

/**
 * Service Events list (`pages/Appointments`).
 *
 * Keys are the column `name`s from `pages/Appointments/columns`.
 */
export const SERVICE_EVENTS_COLUMN_WIDTHS = {
  appointment: { minWidth: defaultWidth, maxWidth: 200 },
  related_event: { minWidth: 380, maxWidth: 440 },
  asset: { minWidth: 235, maxWidth: 290 },
  closed_by_location: { minWidth: wide, maxWidth: 190 },
  po_number: { minWidth: short, maxWidth: 140 },
  ro_number: { minWidth: short, maxWidth: 140 },
  asset_grouping: { minWidth: date, maxWidth: 190 },
  estimate_version: { minWidth: 145, maxWidth: 165 },
  estimate_total: { minWidth: value, maxWidth: date },
  service_provider: { minWidth: wider, maxWidth: 350 },
  elapsed_time: { minWidth: value, maxWidth: date },
  due_date: { minWidth: defaultWidth, maxWidth: wide },
  approval_requested_time: { minWidth: timestamp, maxWidth: 225 },
  // The only column the spec leaves uncapped: an ETR is a fixed-length
  // timestamp, so there is nothing to truncate.
  etr: { minWidth: defaultWidth },
  created: { minWidth: 244, maxWidth: 260 },
  case_created_at: { minWidth: 170, maxWidth: 190 },
  case_updated_at: { minWidth: 175, maxWidth: 195 },
  // Columns the normalization spec does not call out; sized from the scale.
  fleet_status: { minWidth: date, maxWidth: 190 },
  complaint: { minWidth: wider, maxWidth: widest },
  dealer_repair_status: { minWidth: wide, maxWidth: wider },
  follow_up_time: { minWidth: defaultWidth, maxWidth: wide },
  assigned_to: { minWidth: wide, maxWidth: wider },
} as const;

/**
 * Assets list (`pages/Alerts`).
 *
 * Keys are `utils/Asset`'s `COLUMN_NAMES`.
 */
export const ASSETS_COLUMN_WIDTHS = {
  UNIT_NUMBER: { minWidth: defaultWidth, maxWidth: 200 },
  YEAR: { minWidth: tiny, maxWidth: 91 },
  MAKE: { minWidth: short, maxWidth: wide },
  MODEL: { minWidth: 94, maxWidth: 134 },
  ALERTS: { minWidth: 85, maxWidth: 110 },
  WORK_IN_PROGRESS: { minWidth: 170, maxWidth: 190 },
  LOCATION: { minWidth: 232, maxWidth: widest },
  ODOMETER: { minWidth: 133, maxWidth: 170 },
  ENGINE_HOURS: { minWidth: 135, maxWidth: 170 },
  CUSTOMER: { minWidth: wide, maxWidth: widest },
  VIN: { minWidth: 175, maxWidth: 200 },
  CHASSIS_ID: { minWidth: short, maxWidth: date },
  SERIAL_NUMBER: { minWidth: short, maxWidth: date },
  TYPE: { minWidth: wide, maxWidth: widest },
  // Columns the normalization spec does not call out; sized from the scale.
  ENGINE: { minWidth: short, maxWidth: wide },
  FLEET_INFO: { minWidth: wide, maxWidth: widest },
  COST_CENTER: { minWidth: short, maxWidth: date },
} as const;

/**
 * Depots/Customers list (`pages/AssetGroupings`).
 *
 * Keys are the column `name`s from
 * `pages/AssetGroupings/Tabs/components/assetGroupingColumns`.
 */
export const DEPOTS_COLUMN_WIDTHS = {
  NAME: { minWidth: widest, maxWidth: 440 },
  info: { minWidth: badge, maxWidth: 110 },
  PLATFORM_ACCOUNT_ID: { minWidth: wider, maxWidth: 320 },
  CUSTOMER_NUMBER: { minWidth: date, maxWidth: wider },
  CITY_STATE: { minWidth: wider, maxWidth: 280 },
  PHONE_NUMBER: { minWidth: 110, maxWidth: value },
  requests: { minWidth: 250, maxWidth: 320 },
  ENABLED_ASSET_COUNT: { minWidth: compact, maxWidth: short },
  // Columns the normalization spec does not call out; the account columns
  // match Dealer ID and the FMS ID matches Cust #.
  PLATFORM_ACCOUNT: { minWidth: wider, maxWidth: 320 },
  DMS_ID: { minWidth: date, maxWidth: wider },
} as const;

export const serviceEventsColumnWidth = columnWidthsFor<
  keyof typeof SERVICE_EVENTS_COLUMN_WIDTHS
>(SERVICE_EVENTS_COLUMN_WIDTHS);

export const assetsColumnWidth =
  columnWidthsFor<keyof typeof ASSETS_COLUMN_WIDTHS>(ASSETS_COLUMN_WIDTHS);

export const depotsColumnWidth =
  columnWidthsFor<keyof typeof DEPOTS_COLUMN_WIDTHS>(DEPOTS_COLUMN_WIDTHS);
